import type { ReactNode } from "react";
import { NavLink, useLocation, useRouteLoaderData } from "react-router-dom";
import { MobileHeaderActionsSlot } from "@/components/dashboard/header-actions";
import { useQuery } from "@tanstack/react-query";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "@landnest/ui";
import { Home } from "lucide-react";
import { SiteHeader } from "@/components/dashboard/site-header";
import { useNotificationsSocket } from "@/hooks/use-notifications-socket";
import { AdminSidebar } from "@/components/admin/admin-sidebar";
import { PartnerSidebar } from "@/components/partner/partner-sidebar";
import { AccountantSidebar } from "@/components/accountant/accountant-sidebar";
import { ACCOUNTANT_ROLES, ADMIN_ROLES } from "@/lib/auth";
import { api } from "@/lib/api";
import type { AuthResponse, AuthUser } from "@/types/auth";
import type { ProfileResponse } from "@/types/profile";

type AppShellProps = {
  children: ReactNode;
  sidebar: ReactNode;
};

export function AppShell({ children, sidebar }: AppShellProps) {
  useNotificationsSocket();
  return (
    <SidebarProvider
      defaultOpen
      style={
        {
          "--sidebar-width": "calc(var(--spacing) * 72)",
          "--header-height": "calc(var(--spacing) * 12)",
        } as React.CSSProperties
      }
    >
      {sidebar}
      {/* SidebarInset renders a <main> element internally (@landnest/ui),
          so this wrapper must be a plain <div> — otherwise we ship two
          main landmarks per page (axe: landmark-no-duplicate-main). */}
      <SidebarInset className="min-w-0 overflow-hidden">
        <SiteHeader />
        <div id="header-banner" />
        <MobileHeaderActionsSlot />
        <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">{children}</div>
      </SidebarInset>
    </SidebarProvider>
  );
}

/** Convenience shells that resolve the user from the root loader automatically. */

function useCurrentUser(): AuthUser {
  const rootData = useRouteLoaderData("root") as AuthResponse | undefined;
  const base = rootData?.user ?? {
    id: "",
    email: null,
    name: null,
    image: null,
    role: "",
    permissions: [],
    rolePermissions: null,
    partnerProfile: null,
  };
  const { data: profileData } = useQuery<ProfileResponse>({
    queryKey: ["profile"],
    queryFn: async () => {
      const res = await api.get<ProfileResponse>("/profile");
      return res.data;
    },
    enabled: !!base.id,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return {
    ...base,
    name: profileData?.account?.name ?? base.name,
    image: profileData?.account?.image ?? base.image,
  };
}

export function AdminShell({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  const isAccountant = ACCOUNTANT_ROLES.includes(user.role as any);

  return (
    <AppShell sidebar={isAccountant ? <AccountantSidebar user={user} /> : <AdminSidebar user={user} />}>
      {children}
    </AppShell>
  );
}

export function PartnerShell({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  return (
    <AppShell sidebar={<PartnerSidebar user={user} />}>
      {children}
    </AppShell>
  );
}

export function AccountantShell({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  return (
    <AppShell sidebar={<AccountantSidebar user={user} />}>
      {children}
    </AppShell>
  );
}

/**
 * Minimal shell for standalone pages (Profile, Onboarding, Buyer portal)
 * that don't belong to a full admin/partner/accountant portal.
 */
export function GenericShell({
  children,
  title,
  description,
  navItems,
  headerSlot,
}: {
  children: ReactNode;
  title: string;
  description: string;
  navItems: Array<{ to: string; label: string; icon?: React.ComponentType<{ className?: string }> }>;
  headerSlot?: ReactNode;
}) {
  const { pathname } = useLocation();
  return (
    <AppShell
      sidebar={
        <Sidebar collapsible="offcanvas" variant="inset">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                {headerSlot ?? (
                  <div className="flex items-center gap-3 rounded-xl border border-sidebar-border/50 bg-sidebar-accent/30 px-3 py-2.5">
                    <img
                      src="/images/landnest-logo-green.png"
                      alt="Landnest Homes and Properties"
                      className="h-7 w-auto object-contain"
                    />
                    <span className="text-xs font-medium text-sidebar-foreground/70">
                      {description}
                    </span>
                  </div>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    // SidebarMenuButton's `asChild` discards the NavLink's
                    // own active class, so the highlighted state was never
                    // visible. Compute it here and pass via isActive so the
                    // wrapper's data-active styling lights up.
                    const isActive =
                      pathname === item.to ||
                      pathname.startsWith(`${item.to}/`);
                    return (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton asChild isActive={isActive}>
                          <NavLink
                            to={item.to}
                            end={item.to.endsWith("dashboard")}
                          >
                            {Icon ? <Icon className="size-4" /> : <Home className="size-4" />}
                            <span>{item.label}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
      }
    >
      {children}
    </AppShell>
  );
}
